import { z } from 'zod'
import type { PaginatedResponse } from '@/types/common/PaginatedResponse'

export const AccountDetails = z.object({
  id: z.number().int().optional(),
  email_verified: z.boolean().optional().default(false),
  // No default: a missing `active` must never render an account as active
  active: z.boolean().optional(),
  email: z.string().email().min(1),
  name: z.string().optional(),
  surname: z.string().optional(),
  org: z.string().optional(),
  line1: z.string().optional(),
  line2: z.string().optional(),
  city: z.string().optional(),
  postcode: z.string().optional(),
  phone: z.string(),
  country_iso: z.string().max(2).optional(),
  ust_id: z.string().optional(),
  state: z.string().optional(),
  account_type: z.enum(['organization', 'person']).optional(),
  role: z.enum(['admin', 'customer', 'reseller']).optional(),
  totp: z.boolean().default(false),
  lang: z.enum(['de', 'en']).optional().default('de'),
  max_team_members: z.number().int().min(0).optional(),
  max_sub_customers: z.number().int().min(0).optional(),
  // Whether the account may connect AI agents; the backend allows for the
  // pilot being open to every account. Absent reads as no.
  agent_access: z.boolean().optional()
})

export type AccountDetails = z.infer<typeof AccountDetails>

/**
 * Counts for the admin user list's filter pills, computed by the server over
 * the whole search result rather than the page on screen.
 */
export const AccountFacets = z.object({
  total: z.number().int(),
  active: z.number().int(),
  inactive: z.number().int(),
  unverified: z.number().int(),
  by_role: z.object({
    admin: z.number().int(),
    customer: z.number().int(),
    reseller: z.number().int()
  })
})

export type AccountFacets = z.infer<typeof AccountFacets>

/**
 * Which pill the admin user list has active. Every value but `all` is sent to
 * the server as either the `role` or the `status` query parameter.
 */
export type AccountListFilter = 'all' | 'active' | 'inactive' | 'unverified' | 'admin' | 'reseller' | 'customer'

/** The account list envelope: a page plus the counts the filters need. */
export type PaginatedAccounts = PaginatedResponse<AccountDetails> & {
  stats?: AccountFacets
}

/**
 * Request schema for creating a new account (admin only).
 * Omits id and totp which are response-only fields.
 */
export const AccountCreateRequest = z.object({
  email: z.string().email().min(1),
  phone: z.string().min(1),
  name: z.string().optional(),
  surname: z.string().optional(),
  org: z.string().optional(),
  line1: z.string().min(1),
  line2: z.string().optional(),
  city: z.string().min(1),
  postcode: z.string().min(1),
  country_iso: z.string().length(2),
  state: z.string().min(1),
  ust_id: z.string().optional(),
  account_type: z.enum(['organization', 'person']),
  role: z.enum(['admin', 'customer', 'reseller']).optional(),
  active: z.boolean().optional().default(true),
  email_verified: z.boolean().optional().default(false),
  lang: z.enum(['de', 'en']).optional().default('de')
})

export type AccountCreateRequest = z.infer<typeof AccountCreateRequest>

/**
 * Request schema for updating an existing account.
 * All fields are optional since it's a partial update.
 */
export const AccountUpdateRequest = AccountDetails.omit({ id: true, totp: true })

export type AccountUpdateRequest = z.infer<typeof AccountUpdateRequest>
